import { useCallback, useEffect, useState } from "react";
import { CoObjectItem } from "../../interfaces/CoObject";
import { getAllCoObjects } from "../../../utils/helpers/ContentControlHelper";
import { FindItem, useFilteredElaborations } from "../../generic/GenericElaborationSelector";
import { elaborationStatusItemIsBlocking } from "../../../redux/feature/object/remote-elaborations-slice";
import { getTags } from "../../../utils/helpers/TagsHelper";
import { Tag } from "../../interfaces/Tag";
import { DocumentPart } from "../../../api/models";
import { getRules } from "../../../utils/helpers/RulesHelper";
import { Rule } from "../../interfaces/Rule";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks/hooks";
import {
  selectValidationState,
  setIsLoading,
  setRules as setStoreRules,
  setTags as setStoreTags,
  setValidationData,
} from "../../../redux/feature/validation/validation-slice";

export interface ValidationPanelProps {
  documentPart?: DocumentPart;
}

export interface ValidationPanelResult {
  blockingElaboration: any;
  pageIndex: number;
  isLoading: boolean;
  coObjects: CoObjectItem[];
  tags: Tag[];
  updateTags: (tags: Tag[]) => void;
  rules: Rule[];
  updateRules: (rules: Rule[]) => void;
  onClickPivot: (selected: { props: { itemKey: string } }) => void;
}

const findBlockingElaboration = (item: FindItem) =>
  item.elaborationType !== "SHEET_AND_AREA" && elaborationStatusItemIsBlocking(item);

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function useValidationPanel(_props: ValidationPanelProps): ValidationPanelResult {
  const dispatch = useAppDispatch();
  const {
    coObjects,
    tags,
    rules,
    isLoading: storeIsLoading,
    isLoaded,
  } = useAppSelector(selectValidationState);
  const blockingElaboration = useFilteredElaborations({ findFunction: findBlockingElaboration });
  const [pageIndex, setPageIndex] = useState<number>(1);

  useEffect(() => {
    if (isLoaded || storeIsLoading) return;

    const load = async () => {
      try {
        dispatch(setIsLoading(true));
        const filteredBy = ["TABLE", "AMOUNT"];
        const allCoObjects: CoObjectItem[] = await getAllCoObjects(filteredBy);
        const filteredCoObjects: CoObjectItem[] = allCoObjects.filter(
          (obj) => obj.source?.elabResultType !== "IMAGE_STRINGBASE64"
        );
        const [loadedTags, loadedRules] = await Promise.all([
          getTags(filteredCoObjects),
          getRules(),
        ]);
        dispatch(
          setValidationData({
            coObjects: filteredCoObjects,
            tags: loadedTags,
            rules: loadedRules,
          })
        );
      } catch (e) {
        console.error("ValidationPanelHook: error loading coObjects:", e);
        dispatch(setIsLoading(false));
      }
    };
    void load();
  }, [isLoaded, storeIsLoading, dispatch]);

  const updateTags = useCallback(
    (newTags: Tag[]) => {
      console.debug("[ValidationPanelHook] updateTags called with:", newTags);
      dispatch(setStoreTags(newTags));
    },
    [dispatch]
  );

  const updateRules = useCallback(
    (newRules: Rule[]) => {
      console.debug("[ValidationPanelHook] updateRule called with:", newRules);
      dispatch(setStoreRules(newRules));
    },
    [dispatch]
  );

  function onClickPivot(selected: { props: { itemKey: string } }) {
    setPageIndex(Number(selected.props.itemKey));
  }

  return {
    blockingElaboration,
    pageIndex,
    isLoading: !isLoaded || storeIsLoading,
    coObjects,
    tags,
    updateTags,
    rules,
    updateRules,
    onClickPivot,
  };
}
